
import { makeStyles } from 'tss-react/mui';
import { ActionItemStatus, ProviderActivationEventName } from '@uplift/api';
import { useCurrentuser } from '@uplift/contexts';
import { useL10n } from '@uplift/hooks';
import { Card } from '@uplift/icons';
import { useProviderActivationEvents } from '@uplift/queries';
import { LightChip, LinkDashboardActionItem, Tooltip } from '@uplift/ui-v2';

import { getChipColor, hasEvent } from '../../helpers';
import { SelectStatesModal } from '../../pages';
import { useSharedStyles } from '../../styles';
import { Box, Link, Typography } from '@mui/material';
import { ROUTING_CONFIG } from '@uplift/constants';
import { usePracticeStatesData } from '../../../practice-states-page/practice-states-page.hooks';
import { useMemo } from 'react';
import { ProviderMedicalRole } from '@uplift/types';
import { useOnboardingEventActions } from '../../useOnboardingEventActions.hook';
import { useHistory } from 'react-router';

export type SelectStatesProps = {
  status: ActionItemStatus;
  isSelectStatesModalOpen: boolean;
  setIsSelectStatesModalOpen: (show: boolean) => void;
  handleCloseModal: () => void;
};

export const SelectStates: React.VFC<SelectStatesProps> = props => {
  const l10n = useL10n();
  const { classes: sharedClasses } = useSharedStyles();


  if (props.status === ActionItemStatus.todo) {
    return <SelectStatesTodoItem {...props} />;
  }

  return (
    <Box className={sharedClasses.progressSummaryStepWithChip}>
      <Box display="flex" flexDirection="column">
        <Typography variant="h5">{l10n.providerSettings.onboarding.practiceStates}</Typography>
        <Link component="a" href={ROUTING_CONFIG.practiceStates} className={sharedClasses.link}>Update States</Link>
      </Box>
      <PracticeStatesStatusChip />
    </Box>
  );
};

const SelectStatesTodoItem: React.VFC<SelectStatesProps> = ({ isSelectStatesModalOpen, setIsSelectStatesModalOpen, handleCloseModal }) => {
  const l10n = useL10n();
  const { classes: sharedClasses } = useSharedStyles();
  const { classes } = useStyles();
  const me = useCurrentuser();
  const history = useHistory();
  const { data: events } = useProviderActivationEvents(me?.id);
  const isCredentialingApproved = hasEvent(events, ProviderActivationEventName.credentialingApproved);
  const isStateSelectionDisabled = !isCredentialingApproved;
  const { stateSelectionSubmitted } = useOnboardingEventActions(me?.id);

  const onActionClick = async () => {
    if (me.medicalRole === ProviderMedicalRole.psychiatrist) {
      await stateSelectionSubmitted();
      history.push(ROUTING_CONFIG.practiceStates);
      return;
    }

    setIsSelectStatesModalOpen(true);
  };

  return (
    <>
      {isSelectStatesModalOpen && <SelectStatesModal closeModal={handleCloseModal} />}
      <Tooltip
        disableHoverListener={!isStateSelectionDisabled}
        title="Available once approved by credentialing"
        placement="top"
        arrow={false}
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [ 0, -30 ]
                }
              }
            ]
          }
        }}
      >
        <div>
          <LinkDashboardActionItem
            title={l10n.dashboard.actionItems.stateSelection.title}
            body={l10n.dashboard.actionItems.stateSelection.subtitle}
            bodyClassName={!isStateSelectionDisabled && sharedClasses.actionItemSubtitle}
            color={isStateSelectionDisabled ? 'default' : 'danger'}
            icon={<Card />}
            link="#"
            actionText={l10n.providerSettings.onboarding.selectStates}
            onClick={onActionClick}
            className={isStateSelectionDisabled && classes.disabledActionItem}
          />
        </div>
      </Tooltip>
    </>
  );
};

const PracticeStatesStatusChip = () => {
  const l10n = useL10n();
  const { rows } = usePracticeStatesData();

  const status = useMemo(() => {
    const atLeastOneInReview = rows.some(r => r.status === 'inReview');
    const atLeastOneApproved = rows.some(r => r.status === 'live');

    if (atLeastOneApproved) {
      return ActionItemStatus.approved;
    }

    if (atLeastOneInReview) {
      return ActionItemStatus.inReview;
    }

    return ActionItemStatus.todo;
  }, [ rows ]);

  return <LightChip label={l10n.dashboard.actionItems.status[status]} color={getChipColor(status)} size="small" />;
};

const useStyles = makeStyles()(theme => ({
  disabledActionItem: {
    color: theme.palette.text.disabled,
    cursor: 'not-allowed',
    '& a': {
      pointerEvents: 'none',
      backgroundColor: theme.palette.action.disabledBackground,
      color: theme.palette.text.disabled,
    },
  },
}));
